import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { MontadoraInput } from '../types/montadora';
import { toast } from 'react-toastify';

export function useMontadoras() {
  const queryClient = useQueryClient();

  // Query para listar montadoras
  const { data: montadoras, isLoading: listLoading } = useQuery({
    queryKey: ['montadoras'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('montadoras')
        .select('*')
        .order('nome', { ascending: true });

      if (error) throw error;
      return data || [];
    },
  });

  // Query para buscar montadora específica
  const useMontadora = (id: string) => {
    return useQuery({
      queryKey: ['montadoras', id],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('montadoras')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        return data;
      },
      enabled: !!id,
    });
  };

  // Função para fazer upload de logo
  const uploadLogo = async (file: File): Promise<string> => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

    const { error } = await supabase.storage
      .from('logos')
      .upload(fileName, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      throw new Error('Erro ao fazer upload da logo: ' + error.message);
    }

    return fileName;
  };

  // Função para deletar logo
  const deleteLogo = async (logoPath: string) => {
    if (!logoPath) return;
    
    const { error } = await supabase.storage
      .from('logos')
      .remove([logoPath]);

    if (error) {
      console.error('Erro ao deletar logo:', error);
    }
  };

  // Função para obter URL pública da logo
  const getLogoUrl = (logoPath: string | null | undefined): string | null => {
    if (!logoPath) return null;
    
    const { data } = supabase.storage
      .from('logos')
      .getPublicUrl(logoPath);
    
    return data.publicUrl;
  };

  // Mutation para criar montadora
  const { mutateAsync: criarMontadora } = useMutation({
    mutationFn: async ({ montadora, logoFile }: { montadora: MontadoraInput; logoFile?: File }) => {
      let logoPath = '';
      
      if (logoFile) {
        logoPath = await uploadLogo(logoFile);
      }

      const { error } = await supabase
        .from('montadoras')
        .insert([{ ...montadora, logo: logoPath || null }]);

      if (error) {
        // Se deu erro ao criar a montadora, deletar a logo que foi enviada
        if (logoPath) {
          await deleteLogo(logoPath);
        }
        throw error;
      }
      
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['montadoras'] });
      toast.success('Montadora criada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao criar montadora: ' + error.message);
    },
  });

  // Mutation para atualizar montadora
  const { mutateAsync: atualizarMontadora } = useMutation({
    mutationFn: async ({ 
      id, 
      montadora, 
      logoFile, 
      removerLogo = false 
    }: { 
      id: string; 
      montadora: MontadoraInput; 
      logoFile?: File; 
      removerLogo?: boolean;
    }) => {
      // Buscar montadora atual para pegar a logo existente
      const { data: montadoraAtual } = await supabase
        .from('montadoras')
        .select('logo')
        .eq('id', id)
        .single();

      let logoPath = montadoraAtual?.logo || '';

      // Se foi solicitado remover a logo
      if (removerLogo && logoPath) {
        await deleteLogo(logoPath);
        logoPath = '';
      }
      // Se foi enviado um novo arquivo de logo
      else if (logoFile) {
        // Deletar logo anterior se existir
        if (logoPath) {
          await deleteLogo(logoPath);
        }
        
        logoPath = await uploadLogo(logoFile);
      }

      const { error } = await supabase
        .from('montadoras')
        .update({ ...montadora, logo: logoPath || null })
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['montadoras'] });
      queryClient.invalidateQueries({ queryKey: ['montadoras', variables.id] });
      toast.success('Montadora atualizada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao atualizar montadora: ' + error.message);
    },
  });

  // Mutation para excluir montadora
  const { mutateAsync: excluirMontadora } = useMutation({
    mutationFn: async (id: string) => {
      // Buscar a montadora para obter o caminho da logo
      const { data: montadora } = await supabase
        .from('montadoras')
        .select('logo')
        .eq('id', id)
        .single();

      // Deletar logo se existir
      if (montadora?.logo) {
        await deleteLogo(montadora.logo);
      }

      const { error } = await supabase
        .from('montadoras')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return true;
    },
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ['montadoras'] });
      queryClient.invalidateQueries({ queryKey: ['montadoras', id] });
      toast.success('Montadora excluída com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao excluir montadora: ' + error.message);
    },
  });

  return {
    montadoras,
    listLoading,
    useMontadora,
    criarMontadora,
    atualizarMontadora,
    excluirMontadora,
    getLogoUrl,
  };
} 